import { useTheme } from "@mui/material";
import { useMemo } from "react";
import Plot from "components/plotly/LocalisedPlot";
import { useTenantTranslation } from "hooks/formatters";
import { useMaterialColor, useMaterialDisplayName } from "hooks/useMaterials";
import { useMaterials } from "hooks/useMaterials";
import { CrewComparisonData } from "src/store/api/generatedApi";

type MaterialsChartProps = Readonly<{
  data: CrewComparisonData;
}>;

export const MaterialsChart = ({ data }: MaterialsChartProps) => {
  const { t } = useTenantTranslation();
  const theme = useTheme();
  const { getColor } = useMaterialColor();
  const materialDisplayName = useMaterialDisplayName();
  const { materials: materialMetadata } = useMaterials();

  const { plotData, hasCrewData } = useMemo(() => {
    const allCrew = data.crews.find((c) => c.crew_id === "All");
    const otherCrews = data.crews.filter((c) => c.crew_id !== "All");
    const crewIds = otherCrews.map((c) => c.crew_id);
    const xLabels = [t("plannedSource"), t("allCrews"), ...crewIds];

    const orderingByName = new Map(
      (materialMetadata ?? [])
        .slice()
        .sort((a, b) => a.ordering - b.ordering)
        .map((m) => [m.name, m.ordering] as const)
    );

    const allMaterials = [
      ...new Set(
        data.crews.flatMap(({ materials, planned_materials }) => [
          ...materials.map(({ material_name }) => material_name),
          ...planned_materials.map(({ material_name }) => material_name),
        ])
      ),
    ].sort((a, b) => {
      const aOrder = orderingByName.get(a) ?? Number.MAX_SAFE_INTEGER;
      const bOrder = orderingByName.get(b) ?? Number.MAX_SAFE_INTEGER;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return a.localeCompare(b);
    });

    const traces = allMaterials.map((materialName) => {
      const overallPlan = allCrew?.planned_materials;
      const plannedMaterial = overallPlan?.find(
        ({ material_name }) => material_name === materialName
      );
      const plannedPercentage = plannedMaterial
        ? plannedMaterial.fraction * 100
        : 0;

      const allCrewMaterial = allCrew?.materials.find(
        (m) => m.material_name === materialName
      );
      const allCrewPercentage = allCrewMaterial
        ? allCrewMaterial.fraction * 100
        : 0;

      const crewPercentages = otherCrews.map((crew) => {
        const material = crew.materials.find(
          (m) => m.material_name === materialName
        );
        return material ? material.fraction * 100 : 0;
      });

      return {
        x: xLabels,
        y: [plannedPercentage, allCrewPercentage, ...crewPercentages],
        name: materialDisplayName(materialName),
        type: "bar" as const,
        marker: {
          color: getColor(materialName),
        },
        hovertemplate: `%{x}<br>${materialDisplayName(materialName)}: %{y:.1f}%<extra></extra>`,
      };
    });

    return { plotData: traces, hasCrewData: otherCrews.length > 0 };
  }, [data, getColor, materialMetadata, t, materialDisplayName]);

  return (
    <Plot
      data={plotData}
      layout={{
        autosize: true,
        barmode: "stack",
        xaxis: {
          title: { text: t("crewId") },
          type: "category",
        },
        yaxis: {
          title: { text: t("proportionOfLifts") },
          ticksuffix: "%",
          range: [0, 100],
        },
        margin: {
          l: 70,
          r: 120,
          t: 40,
          b: 60,
        },
        legend: {
          x: 1.02,
          y: 1,
          xanchor: "left",
          title: { text: t("material") },
        },
        hovermode: "x unified",
        shapes: hasCrewData
          ? [
              {
                type: "line",
                x0: 1.5,
                x1: 1.5,
                y0: 0,
                y1: 100,
                line: {
                  color: theme.palette.grey[500],
                  width: 1.5,
                  dash: "dot",
                },
              },
            ]
          : [],
      }}
      config={{
        responsive: true,
        displayModeBar: true,
        displaylogo: false,
      }}
      style={{ width: "100%", height: "400px" }}
      useResizeHandler
    />
  );
};
